// browser_keys_view — PURE helpers for the owner's Browser keys panel.
//
// Kept out of the React component for the reason secrets_view gives, and one more: every
// decision here is one a mistake would be expensive in. Validating a paste, deciding what
// the field is allowed to send, and saying out loud that a key is not live until the next
// deploy are the three places this panel can mislead an owner.
//
// ── WHY THE RULES COME FROM THE ROW, NOT FROM HERE ──────────────────────────
//
// The vendor's shapes (`pk_live_` / `sk_`, `pk.` / `sk.`) live in ONE table, on the server
// (platform-service/lib/frontend_keys.ts), and each row carries its own. A second copy here
// would drift, and a drifted copy either refuses the owner's real key or accepts the secret
// twin into a file every visitor downloads. So this module applies what it was handed and
// nothing more — and the server judges again on write, so the panel is a courtesy, never the
// authority.
import type { BrowserKeyRow } from "../api";

// The same bound the server enforces (frontend_keys.MAX_KEY_LENGTH). Stated as a number
// rather than imported because platform-ui is a separate bundle; the drift that matters — the
// per-vendor shapes — is carried in the row instead of copied.
export const MAX_BROWSER_KEY_LEN = 512;

export type ValueCheck = { ok: true; value: string } | { ok: false; error: string };

/**
 * Validate what the owner pasted, with the row's own shapes.
 *
 * It does NOT trim. A trailing newline or space is the commonest paste artefact from a vendor
 * dashboard, and the server refuses it rather than trimming — the build file it lands in
 * cannot hold a line break, and a silently trimmed value would leave the owner thinking they
 * pasted a whole key when they pasted half of one. So the panel says the same thing.
 */
export function validateBrowserKeyValue(row: BrowserKeyRow, raw: string): ValueCheck {
  if (raw.length === 0) return { ok: false, error: "Enter a value." };
  if (raw.length > MAX_BROWSER_KEY_LEN) {
    return { ok: false, error: `That is ${raw.length} characters — longer than any key this platform accepts (${MAX_BROWSER_KEY_LEN}).` };
  }
  if (!/^[\x21-\x7e]+$/.test(raw)) {
    return { ok: false, error: "Paste the key on its own — this value has a space, a line break, or a character that cannot go into the app's build." };
  }
  for (const bad of row.neverStartsWith) {
    if (raw.startsWith(bad)) {
      const take = row.mustStartWith.length > 0 ? `the key that starts with “${row.mustStartWith[0]}”` : "the publishable key";
      return {
        ok: false,
        error: `This is your ${row.vendor} SECRET key — it starts with “${bad}”, and this value goes into your app's public code. Use ${take} instead. A secret key belongs in Secrets, where your app never sees its value.`,
      };
    }
  }
  if (row.mustStartWith.length > 0 && !row.mustStartWith.some((p) => raw.startsWith(p))) {
    const list = row.mustStartWith.map((p) => `“${p}”`).join(" or ");
    return { ok: false, error: `A ${row.vendor} key for the browser starts with ${list}.` };
  }
  return { ok: true, value: raw };
}

/** Unset keys first — they are what the owner has to act on — then by variable name. */
export function sortBrowserKeys(rows: readonly BrowserKeyRow[]): BrowserKeyRow[] {
  return [...rows].sort((a, b) => {
    if (a.isSet !== b.isSet) return a.isSet ? 1 : -1;
    return a.env.localeCompare(b.env);
  });
}

/** The one-line state of a row, for the owner. */
export function browserKeyStatusLabel(row: BrowserKeyRow): string {
  return row.isSet ? "Set — compiled into the app at its next deploy" : "Not set — this part of the app renders nothing";
}

/**
 * What to tell the owner after a successful save. A browser key is compiled in at BUILD
 * time, so a value set now reaches visitors only at the next deploy or publish. Without this
 * sentence an owner sets the key, reloads their app, sees the same blank map, and concludes
 * the platform is broken.
 */
export function afterSaveNote(env: "dev" | "prod"): string {
  return env === "prod"
    ? "Saved. Publish the app for this key to reach your visitors — it is compiled in at build time."
    : "Saved. Deploy the app again for this key to reach the preview — it is compiled in at build time.";
}

/**
 * Is the panel's empty state a real "nothing to do", or does this app simply read no key?
 * Two different sentences: an owner staring at an empty panel while their map is blank needs
 * to know the app never asked for a key, not that they are done.
 */
export function emptyStateText(rows: readonly BrowserKeyRow[]): string | null {
  return rows.length === 0
    ? "This app's UI reads no browser key. Nothing to set here — if a map or a payment form is blank, the app is not asking for a key at all, which is something to raise in the chat."
    : null;
}

/** How many rows still need a value — the number the panel's header shows. */
export function unsetCount(rows: readonly BrowserKeyRow[]): number {
  return rows.filter((r) => !r.isSet).length;
}

/**
 * PURE. Le nom d'hôte d'une adresse de fournisseur, ou l'adresse telle quelle.
 *
 * POURQUOI CE GARDE EXISTE. Le panneau affichait `new URL(row.where).host` sans filet.
 * `new URL` LÈVE sur ce qui n'est pas une adresse absolue — et une exception pendant le
 * rendu d'une ligne emporte TOUT le panneau : écran blanc, aucun message, et les autres clés
 * invisibles avec elle.
 *
 * Trouvé le 2026-10-04 par l'audit de largeurs, qui a chargé le panneau avec une valeur
 * non-URL. Les valeurs du serveur sont bien des URL absolues aujourd'hui
 * (platform-service/lib/frontend_keys.ts) — donc ce n'est pas un défaut visible, c'est une
 * ligne de plus à ajouter à cette table pour qu'il le devienne.
 */
export function vendorHost(where: unknown): string {
  if (typeof where !== "string" || where.trim() === "") return "";
  try { return new URL(where).host || where; } catch { return where; }
}
